import { DATASETS } from '../../../components/Constants';
import { Items, Section } from '../../../types';

export const ALL_RESULTS_TAB = 'ALL';
export const CATEGORY_PREVIEW_COUNT = 4;

export const CATEGORY_META: Record<string, { label: string }> = {
  [DATASETS.DISEASES]: { label: 'Diseases' },
  [DATASETS.GENES]: { label: 'Genes' },
  [DATASETS.VARIANTS]: { label: 'Variants' },
  [DATASETS.CHEMICALS]: { label: 'Chemicals' },
};

export type DropdownTab = {
  id: string;
  label: string;
  count: number;
  showDot: boolean;
  category?: string;
};

export type VisibleSection = {
  title: string;
  total: number;
  preview: any[];
  truncated: boolean;
};

export type NavigableEntry =
  | { type: 'item'; item: any; sectionTitle: string }
  | { type: 'seeAll'; sectionTitle: string; count: number };

function getSearchCatalog(data: any) {
  return {
    [DATASETS.DISEASES]: data?.diseases,
    [DATASETS.GENES]: data?.genes,
    [DATASETS.VARIANTS]: data?.variants,
    [DATASETS.CHEMICALS]: data?.chemicals,
  };
}

function countLabel(value: unknown, singular: string, plural: string): string | null {
  if (value == null || value === '') return null;
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return null;
  return `${n} ${n === 1 ? singular : plural}`;
}

export function getCategoryLabel(title: string) {
  return CATEGORY_META[title]?.label ?? title;
}

export function getDefaultActiveTab(sections: Section[]): string {
  if (sections.length > 1) return ALL_RESULTS_TAB;
  return sections[0]?.title ?? ALL_RESULTS_TAB;
}

export function resolveActiveTab(sections: Section[], activeTab: string): string {
  if (activeTab === ALL_RESULTS_TAB && sections.length > 1) return ALL_RESULTS_TAB;
  if (sections.some(section => section.title === activeTab)) return activeTab;
  return getDefaultActiveTab(sections);
}

export function getDropdownTabs(sections: Section[]): DropdownTab[] {
  const total = sections.reduce((sum, section) => sum + (section.data?.length ?? 0), 0);
  const tabs: DropdownTab[] = [];

  if (sections.length > 1) {
    tabs.push({ id: ALL_RESULTS_TAB, label: 'All results', count: total, showDot: false });
  }

  sections.forEach(section => {
    tabs.push({
      id: section.title,
      label: getCategoryLabel(section.title),
      count: section.data?.length ?? 0,
      showDot: true,
      category: section.title,
    });
  });

  return tabs;
}

export function getVisibleSections(
  sections: Section[],
  activeTab: string,
  previewCount = CATEGORY_PREVIEW_COUNT,
): VisibleSection[] {
  const resolved = resolveActiveTab(sections, activeTab);
  if (resolved === ALL_RESULTS_TAB) {
    return sections.map(section => ({
      title: section.title,
      total: section.data.length,
      preview: section.data.slice(0, previewCount),
      truncated: section.data.length > previewCount,
    }));
  }

  const section = sections.find(item => item.title === resolved);
  if (!section) return [];

  return [
    {
      title: section.title,
      total: section.data.length,
      preview: section.data,
      truncated: false,
    },
  ];
}

export function buildNavigableEntries(visibleSections: VisibleSection[]): NavigableEntry[] {
  const entries: NavigableEntry[] = [];
  visibleSections.forEach(section => {
    section.preview.forEach(item => {
      entries.push({ type: 'item', item, sectionTitle: section.title });
    });
    if (section.truncated) {
      entries.push({ type: 'seeAll', sectionTitle: section.title, count: section.total });
    }
  });
  return entries;
}

function escapeRegExp(value: string) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function getPrimaryLabel(item: any): string {
  return String(item?.chemPrefName || item?.symbol || item?.strID || item?.name || item?.id || '').trim();
}

export function getItemRelevanceScore(item: any, query: string): number {
  const q = query.trim().toLowerCase();
  if (!q) return 0;

  const apiScore = Number(item?.score ?? item?._score ?? item?.relevanceScore);
  if (Number.isFinite(apiScore)) {
    return 100000 + apiScore;
  }

  const primary = getPrimaryLabel(item).toLowerCase();
  const highlightField = String(item?.highlightFields?.[0] ?? '');
  const highlightedHtml = String(item?.highlightFields?.[1] ?? '');
  const highlightedText = highlightedHtml.replaceAll('<em>', '').replaceAll('</em>', '').toLowerCase();
  const emFragments = [...highlightedHtml.matchAll(/<em>(.*?)<\/em>/gi)].map(match => match[1].toLowerCase());

  let score = 0;

  if (primary === q) score += 1000;
  else if (primary.startsWith(q)) score += 850;
  else if (new RegExp(`(^|[^a-z0-9])${escapeRegExp(q)}`).test(primary)) score += 700;
  else if (primary.includes(q)) score += 550;

  if (primary.includes(q) && primary.length > 0) {
    score += (q.length / primary.length) * 120;
  }

  emFragments.forEach(fragment => {
    if (fragment === q) score += 200;
    else if (fragment.startsWith(q)) score += 160;
    else if (fragment.includes(q)) score += 80;
  });

  if (!primary.includes(q) && highlightedText) {
    if (highlightedText === q) score += 320;
    else if (highlightedText.startsWith(q)) score += 260;
    else if (highlightedText.includes(q)) score += 140;

    if (/synonym/i.test(highlightField)) {
      score *= 0.65;
    }
  }

  return score;
}

export function rankSectionsByRelevance(sections: Section[], query: string): Section[] {
  const ranked = sections.map(section => ({
    ...section,
    data: [...(section.data ?? [])].sort(
      (a, b) => getItemRelevanceScore(b, query) - getItemRelevanceScore(a, query),
    ),
  }));

  return ranked.sort((a, b) => {
    const scoreA = a.data.reduce((max, item) => Math.max(max, getItemRelevanceScore(item, query)), 0);
    const scoreB = b.data.reduce((max, item) => Math.max(max, getItemRelevanceScore(item, query)), 0);
    return scoreB - scoreA;
  });
}

export function highlightQueryParts(text: string, query: string): { text: string; match: boolean }[] {
  if (!text) return [];
  const needle = query.trim();
  if (!needle) return [{ text, match: false }];

  const escaped = needle.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const parts = text.split(new RegExp(`(${escaped})`, 'gi'));
  return parts.filter(Boolean).map(part => ({
    text: part,
    match: part.toLowerCase() === needle.toLowerCase(),
  }));
}

export function getItemPresentation(item: any): { title: string; subtitle: string; tags: string[] } {
  switch (item?.dataset) {
    case DATASETS.DISEASES:
      return {
        title: item.name ?? '',
        subtitle: item.diseaseUMLSCUI || item.id ? `CUI ${item.diseaseUMLSCUI ?? item.id}` : '',
        tags: [
          countLabel(item.numGenesAssociatedToDisease_ALL, 'gene', 'genes'),
          countLabel(item.numVariantsAssociatedToDisease_ALL, 'variant', 'variants'),
        ].filter((tag): tag is string => Boolean(tag)),
      };
    case DATASETS.GENES:
      return {
        title: item.symbol ?? '',
        subtitle: item.ncbiID ? `Entrez ${item.ncbiID}` : (item.description ?? ''),
        tags: [
          countLabel(item.numDiseasesAssociatedToGene_ALL, 'disease', 'diseases'),
          countLabel(item.numVariantsAssociatedToGene_ALL, 'variant', 'variants'),
        ].filter((tag): tag is string => Boolean(tag)),
      };
    case DATASETS.VARIANTS:
      return {
        title: item.strID ?? '',
        subtitle: item.geneSymbol ?? '',
        tags: [countLabel(item.numDiseasesAssociatedToVariant_ALL, 'disease', 'diseases')].filter(
          (tag): tag is string => Boolean(tag),
        ),
      };
    case DATASETS.CHEMICALS:
      return {
        title: item.chemPrefName ?? '',
        subtitle: item.chemID ?? '',
        tags: [
          countLabel(item.chemNumPublications, 'publication', 'publications'),
          countLabel(item.chemNumDiseases, 'disease', 'diseases'),
        ].filter((tag): tag is string => Boolean(tag)),
      };
    default:
      return { title: String(item?.id ?? ''), subtitle: '', tags: [] };
  }
}

export function getMatchSnippet(item: any): string | null {
  const raw = item?.highlightFields?.[1];
  if (!raw) return null;
  const cleaned = String(raw).replaceAll('<em>', '').replaceAll('</em>', '').trim();
  if (!cleaned) return null;
  return `matched '${cleaned}'`;
}

export function getSelectionTagLabel(item: any): string {
  const title = getItemPresentation(item).title?.trim();
  if (title) return title;
  return String(item?.id ?? '');
}

// SearchBar tags
export const tagMapping = (item: Items) => {
  const tooltipMappings = {
    [DATASETS.DISEASES]: item.name + ', ' + item.diseaseUMLSCUI,
    [DATASETS.GENES]: item.symbol + ', ' + item.description,
    [DATASETS.VARIANTS]: item.strID + ', ' + item.geneSymbol,
    [DATASETS.CHEMICALS]: item.chemPrefName + ', ' + item.chemID,
  };
  const toolTip = tooltipMappings[item.dataset];

  const textContentMappings = {
    [DATASETS.DISEASES]: 'Name: ' + item.name + ', ' + 'CUI: ' + item.diseaseUMLSCUI,
    [DATASETS.GENES]: `Symbol: ${item.symbol}, ${item.description ? 'Description:' + item.description + ',' : ''} EntrezId: ${item.ncbiID}`,
    [DATASETS.VARIANTS]: 'SNPdb: ' + item.strID + ', ' + 'Gene Symbol: ' + item.geneSymbol,
    [DATASETS.CHEMICALS]: 'Name: ' + item.chemPrefName + ', ID: ' + item.chemID,
  };
  const textContent = textContentMappings[item.dataset];

  return {
    tooltip: toolTip,
    text: textContent,
  };
};

// SearchBar items show
export const handleSearchItems = (searchItems, data) => {
  const sections: Section[] = [];
  const catalog = getSearchCatalog(data);

  if (!searchItems?.length) {
    Object.entries(catalog).forEach(([title, items]) => {
      if (items?.length > 0) sections.push({ title, data: items });
    });
    return sections;
  }

  const dataset = searchItems[0]?.dataset;
  if (dataset && catalog[dataset]?.length > 0) {
    sections.push({ title: dataset, data: catalog[dataset] });
    return sections;
  }

  const keyMap = {
    diseaseID: DATASETS.DISEASES,
    diseaseUMLSCUI: DATASETS.DISEASES,
    geneID: DATASETS.GENES,
    strID: DATASETS.VARIANTS,
    chemID: DATASETS.CHEMICALS,
  };

  const matchKey = Object.keys(searchItems[0] ?? {}).find(key => keyMap[key]) as string;
  const sectionTitle = keyMap[matchKey];

  if (sectionTitle && catalog[sectionTitle]?.length > 0) {
    sections.push({ title: sectionTitle, data: catalog[sectionTitle] });
  }

  return sections;
};

// SectionItem items naming
// Name
export const formatStrName = (item: Items) => {
  switch (item.dataset) {
    case DATASETS.DISEASES:
      return 'Name: ' + item.name + ', CUI: ' + item.id;
    case DATASETS.GENES:
      return 'Symbol: ' + item.symbol + ', Description: ' + item.description + ', EntrezId: ' + item.ncbiID;
    case DATASETS.VARIANTS:
      return 'SNPdb id: ' + item.strID + ', Gene Symbol: ' + item.geneSymbol;
    case DATASETS.CHEMICALS:
      return 'Name: ' + item.chemPrefName + ', ID: ' + item.chemID;
    default:
      break;
  }
};

// Count
export const formatStrCount = (item: Items) => {
  switch (item.dataset) {
    case DATASETS.DISEASES:
      return (
        'N. genes: ' +
        item.numGenesAssociatedToDisease_ALL +
        ', N. snps: ' +
        item.numVariantsAssociatedToDisease_ALL
      );
    case DATASETS.GENES:
      return (
        'N. diseases: ' +
        item.numDiseasesAssociatedToGene_ALL +
        ', N. snps: ' +
        item.numVariantsAssociatedToGene_ALL
      );
    case DATASETS.VARIANTS:
      return 'N. diseases: ' + item.numDiseasesAssociatedToVariant_ALL;
    case DATASETS.CHEMICALS:
      return 'N. PMIDS: ' + item.chemNumPublications;
    default:
      break;
  }
};

// Match
export const formatStrMatch = (item: Items) => {
  let match = 'Match: ';
  if (item.highlightFields && item.highlightFields[1]) {
    match += item.highlightFields[1].replaceAll('<em>', '').replaceAll('</em>', '');
  } else {
    match += 'No match information available';
  }
  return match;
};
